import { effect, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT } from '@angular/common';

export type AppTheme = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'wm-theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);

  readonly theme = signal<AppTheme>(this.loadTheme());

  constructor() {
    effect(() => {
      const theme = this.theme();
      this.applyTheme(theme);
      if (isPlatformBrowser(this.platformId)) {
        localStorage.setItem(STORAGE_KEY, theme);
      }
    });
  }

  toggle(): void {
    const current = this.theme();
    if (current === 'system') this.theme.set('light');
    else if (current === 'light') this.theme.set('dark');
    else this.theme.set('system');
  }

  private loadTheme(): AppTheme {
    if (!isPlatformBrowser(this.platformId)) return 'system';
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    return 'system';
  }

  private applyTheme(theme: AppTheme): void {
    const root = this.document?.documentElement;
    if (!root) return;
    if (theme === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', theme);
    }
  }
}
